import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { handleApiResponse, queryKeys, sharedApi } from "@habits/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

const linkUrl = (provider: ProviderId) =>
  `${SERVER_BASE_URL.replace(/\/$/u, "")}/api/auth/link-social/${provider}`;

/**
 * "Connected accounts": lets you link/unlink Google and Apple from the profile.
 * It only shows providers ENABLED in the backend. It respects the last-method
 * safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Read ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/CC-60 — WHY THESE `queryFn`s GO THROUGH `handleApiResponse`:
  // `openapi-fetch` NEVER throws: returns `{ data, error }`. These two queries
  // did `return data ?? { … }`, so a 500 resolved in **SUCCESS with a
  // fabricated object** and card asserted "Google · Not connected" (with its
  // "Connect" button) for an account that could be LINKED. Measured with
  // browser: `login-methods` at sustained 500 (`retry: false` → single
  // attempt, permanent lie) and `social/providers` at 200 → section was
  // rendered completely and lied, without any error state. `handleApiResponse`
  // THROWS on any non-2xx → `isError` becomes LIVE and below the failure
  // is painted instead of inventing the state of the account.
  //
  // NOTE: this does NOT apply to `useSession()` (`lib/auth.ts`), which must continue
  // swallowing the error: its `?? null` is what supports guest mode.
  const providersQuery = useQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => handleApiResponse(sharedApi.socialProviders(api)),
    staleTime: 5 * 60 * 1000,
    retry: false,
  }));

  const methodsQuery = useQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => handleApiResponse(sharedApi.loginMethods(api)),
    retry: false,
  }));

  /** Did either load fail? Then we do NOT know the real state. */
  const loadFailed = () => providersQuery.isError || methodsQuery.isError;

  const retryAll = () => {
    void providersQuery.refetch();
    void methodsQuery.refetch();
  };

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · I2: `.data` is the ONLY field that suspends on solid-query's
    // proxy, and suspends while value is absent — guard must come
    // BEFORE access, not after.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · I2: without `isSuccess` guard, with `login-methods` down
  // `data` is `undefined`, `?? []` turns it into "none linked" and
  // screen asserts "Not connected" for a Google account that IS connected.
  // A network failure cannot become an assertion about the account.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      // CC-30: openapi-fetch resolves on 4xx/5xx, so wrap the unlink in
      // handleApiResponse to make a rejected request throw (with the body
      // message) instead of leaking through as a silent success.
      await handleApiResponse(sharedApi.unlinkAccount(api, provider));
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("only sign-in method")) {
        showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
      } else {
        showToast(t(msg`Could not unlink the account. Please try again.`), "error");
      }
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback when returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    // Shown if any provider is enabled OR if loading failed. Without the
    // second condition, a `social/providers` failure hides the entire
    // card and error state becomes UNREACHABLE via another route (also
    // the measurement trap: taking down both endpoints gives FALSE RED).
    <Show when={enabledProviders().length > 0 || loadFailed()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23 / CC-60 · I3: "no response" != "no items". On error
              `isLoading`/`isPending` are FALSE, so the loading guard lets through,
              `data` is `undefined`, and `?? []` turns it into an empty list: the
              screen says "no items" when the API simply did not respond.
              The block is WRAPPED; chaining `&& !isError` would be WRONG, because on
              error <Show> would render its fallback, leaving both the error box AND the
              skeleton at the same time, permanently.

              PHASE 2: condition is `loadFailed()` (BOTH queries), not just
              `methodsQuery`. A `social/providers` failure leaves
              `enabledProviders()` empty, and without counting it as failure <Show>
              above hid the ENTIRE card: neither error nor content. */}
          <Show when={loadFailed()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              title={t(msg`Could not load your connected accounts`)}
              description={t(
                msg`We can't tell which providers are linked right now, so nothing is shown as connected or disconnected.`,
              )}
              onRetry={retryAll}
            />
          </Show>
          <Show when={!loadFailed()}>
            <Show
              when={!methodsQuery.isPending && !providersQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
